import React from 'react';

/**
 * Configuration options for scroll spy behavior
 */
export interface ScrollSpyConfig {
  /** Offset from top/bottom edges to trigger first/last section (default: 100px) */
  edgeOffset?: number;
  /** Whether to use throttling for scroll events (default: true) */
  isThrottling?: boolean;
  /** Throttle delay in milliseconds (default: 16ms ≈ 60fps) */
  throttleDelay?: number;
}

/**
 * Custom hook for scroll spy functionality
 * Automatically updates active tab/section based on scroll position
 *
 * @param sectionRefs - Array of refs to section elements
 * @param setActiveTab - Function to update active tab index
 * @param sectionsLength - Number of sections (used for validation)
 * @param config - Optional configuration for scroll spy behavior
 *
 * @example
 * const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
 * const [activeTab, setActiveTab] = useState(0);
 *
 * useScrollSpy(sectionRefs, setActiveTab, sections.length);
 */
export const useScrollSpy = (
  sectionRefs: React.MutableRefObject<(HTMLDivElement | null)[]>,
  setActiveTab: (index: number) => void,
  sectionsLength: number,
  config: ScrollSpyConfig = {},
) => {
  const {
    edgeOffset = 100,
    isThrottling = true,
    throttleDelay = 16, // ~60fps
  } = config;

  // Throttling state for performance
  const lastScrollTime = React.useRef(0);

  /**
   * Determines which section is currently in view based on scroll position
   */
  const determineActiveSection = React.useCallback(() => {
    if (sectionsLength === 0) return 0;

    const scrollPosition = window.scrollY + window.innerHeight / 2; // Middle of viewport
    let activeIndex = 0;

    // Find which section is currently in the middle of the viewport
    sectionRefs.current.forEach((section, index) => {
      if (section) {
        const sectionTop = section.offsetTop;
        const sectionBottom = sectionTop + section.offsetHeight;

        // If scroll position is within this section, update active tab
        if (scrollPosition >= sectionTop && scrollPosition <= sectionBottom) {
          activeIndex = index;
        }
      }
    });

    // Special handling for top and bottom of page
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;

    // If at the very top, select first tab
    if (window.scrollY <= edgeOffset) {
      activeIndex = 0;
    }
    // If at the very bottom, select last tab
    else if (window.scrollY + windowHeight >= documentHeight - edgeOffset) {
      activeIndex = sectionsLength - 1;
    }

    return activeIndex;
  }, [sectionRefs, sectionsLength, edgeOffset]);

  /**
   * Scroll event handler with optional throttling
   */
  const handleScrollSpy = React.useCallback(() => {
    // Throttling for performance
    if (isThrottling) {
      const now = Date.now();
      if (now - lastScrollTime.current < throttleDelay) {
        return;
      }
      lastScrollTime.current = now;
    }

    const activeIndex = determineActiveSection();
    setActiveTab(activeIndex);
  }, [determineActiveSection, setActiveTab, isThrottling, throttleDelay]);

  /**
   * Set up scroll spy listeners
   */
  React.useEffect(() => {
    // Add scroll listener for scroll spy functionality
    window.addEventListener('scroll', handleScrollSpy, { passive: true });

    // Initial check to set correct active tab
    const initialActiveIndex = determineActiveSection();
    setActiveTab(initialActiveIndex);

    return () => {
      window.removeEventListener('scroll', handleScrollSpy);
    };
  }, [handleScrollSpy, determineActiveSection, setActiveTab]);

  // Return the current active section determination function for manual use
  return {
    /** Manually determine which section should be active (useful for debugging) */
    getCurrentActiveSection: determineActiveSection,
  };
};
